import Link from 'next/link'
import { FunctionComponent } from 'react'
import styled from 'styled-components'

import Anchor from 'components/GlobalStyles/Anchor'
import Spacer from 'components/Spacer'

import { formatAddress } from 'utils/listingHelpers'
import { addCommas } from 'utils/stringFormatting'

import * as T from 'types'
import * as E from 'types/enums'

import CardWithInfoSection from './CardWithInfoSection'

const InfoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const InfoRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`

const Price = styled.div`
  font-style: normal;
  font-weight: 900;
  font-size: 18px;
  line-height: 25px;
  color: ${props => props.theme.colors.black};
`

const Info = styled.span`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 14px;
  color: ${props => props.theme.colors.black};
  margin-right: 4px;
`

const StaticInfo = styled.span`
  font-weight: 400;
  line-height: 14px;
  margin-right: 12px;
`

const Address = styled.div`
  font-style: normal;
  font-size: 16px;
  line-height: 22px;
  color: ${props => props.theme.colors.black};
  overflow: hidden;
  text-overflow: ellipsis;
`

interface IProps {
  listing: T.IListing
  showSaleStatus?: boolean
  infoHeight?: number
  imageHeight?: number
  soldListing?: boolean
  externalLink?: boolean
  skipPhotoFetch?: boolean
  renderOverlays?(listing: T.IListing): JSX.Element | null
  onClick?(): void
}

const ViewListingCard: FunctionComponent<IProps> = ({
  listing,
  showSaleStatus,
  infoHeight,
  imageHeight,
  soldListing,
  externalLink,
  skipPhotoFetch = false,
  renderOverlays,
  onClick,
}) => {
  let bath = listing.bath ? listing.bath : 0

  if (listing.bathHalf) {
    bath += listing.bathHalf ? 0.5 : 0
  }

  const renderCard = () => (
    <CardWithInfoSection
      barText={showSaleStatus ? listing?.status?.toString() ?? '' : undefined}
      infoHeight={infoHeight}
      imageHeight={imageHeight}
      listing={listing}
      padding={15}
      renderOverlays={renderOverlays}
      skipPhotoFetch={skipPhotoFetch}
    >
      <InfoContainer>
        <Price>
          {listing?.displayPrice ? `$${addCommas(listing?.displayPrice.toString())}` : ''}
        </Price>
        <Spacer size={10} />
        <InfoRow>
          {listing.propertyType !== E.HomeTypeEnum.land
            ? (
              <>
                <Info>{listing.bed ? addCommas(listing.bed) : 0}</Info>
                <StaticInfo>Bed</StaticInfo>
                <Info>{addCommas(bath)}</Info>
                <StaticInfo>Bath</StaticInfo>
                <Info>{listing.sqft ? addCommas(listing.sqft) : 0}</Info>
                <StaticInfo>Sqft</StaticInfo>
              </>
            )
            : (
              <>
                <Info>{listing.lotSize || 0}</Info>
                <StaticInfo>{listing.lotSizeType || 'Acres'}</StaticInfo>
              </>
            )}
        </InfoRow>
        <Spacer size={10} />
        <Address>{formatAddress(listing, true)}</Address>
      </InfoContainer>
    </CardWithInfoSection>
  )

  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  if (onClick) return <div onClick={onClick}>{renderCard()}</div>

  return (
    <Link
      href={{ pathname: '/listing/[id]', query: { isSold: soldListing } }}
      as={`/listing/${listing.customLinkUrl}`}
      key={listing._id}
      prefetch={false}
      passHref
    >
      <Anchor target={externalLink ? '_blank' : '_self'}>{renderCard()}</Anchor>
    </Link>
  )
}

export default ViewListingCard
